/**
 * SettingsPanel - Individual setting cards design
 *
 * Design principles:
 * - Each setting gets its own distinct card
 * - Cards have subtle shadow and rounded corners
 * - Clean, spacious layout with visual separation
 * - No grouped sections - flat list of setting cards
 */

import { makeStyles, tokens, Text, Spinner, SpinButton, Field, Switch, MessageBar, MessageBarBody } from '@coreai/fluentui-react';
import { useMemo, useCallback } from 'react';
import { ErrorCircleRegular } from '@fluentui/react-icons';
import {
  BooleanRenderer,
  TimeoutRenderer,
  RetryPolicyRenderer,
  ConcurrencyRenderer,
  RunAfterRenderer,
  TrackedPropertiesRenderer,
  SplitOnRenderer,
  CorrelationRenderer,
  PagingRenderer,
  RequestOptionsRenderer,
  ChunkingRenderer,
  CountRenderer,
  ConditionsRenderer,
} from './renderers';
import type {
  NodeSettings,
  SettingData,
  RetryPolicy,
  ConcurrencySettings,
  RunAfterSettings,
  SimpleSetting,
  CorrelationSettings,
  PagingSettings,
  RequestOptions,
  UploadChunk,
} from '@/types/nodeSettings';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  scrollContainer: {
    flex: 1,
    overflow: 'auto',
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXL,
    display: 'flex',
    flexDirection: 'column',
  },
  // Flat setting row — divider-separated, no card borders
  settingCard: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorSubtleBackgroundHover,
    },
  },
  // Section group header (General, Networking, Security, etc.)
  sectionHeader: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  // Download chunk specific row
  downloadChunkRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  downloadChunkLabel: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  downloadChunkControls: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  downloadChunkUnit: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  // Empty and loading states
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: tokens.spacingHorizontalS,
  },
  // Error banner
  errorBanner: {
    margin: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalS,
    marginBottom: 0,
  },
  errorIcon: {
    marginRight: tokens.spacingHorizontalXS,
  },
});

export interface SettingsPanelProps {
  /** Current node settings */
  settings: NodeSettings;
  /** Callback when any setting changes */
  onChange: (settings: NodeSettings) => void;
  /** Whether settings are read-only */
  readOnly?: boolean;
  /** Whether this is for a trigger (affects some UI) */
  isTrigger?: boolean;
  /** Available predecessor actions for run after */
  availablePredecessors?: string[];
  /** Whether settings are currently loading */
  isLoading?: boolean;
  /** Error message if settings failed to load */
  error?: string | null;
  /** Validation errors for settings (key → error message) */
  validationErrors?: Record<string, string>;
}

/**
 * SettingsPanel component for displaying and editing node settings
 */
export const SettingsPanel = ({
  settings,
  onChange,
  readOnly = false,
  isTrigger = false,
  availablePredecessors = [],
  isLoading = false,
  error = null,
  validationErrors = {},
}: SettingsPanelProps) => {
  const styles = useStyles();

  // Memoized helper to create onChange handlers for specific settings
  const createChangeHandler = useCallback(<T,>(key: keyof NodeSettings) => {
    return (value: T | undefined) => {
      const currentSetting = settings[key] as SettingData<T> | undefined;
      onChange({
        ...settings,
        [key]: {
          ...currentSetting,
          isSupported: currentSetting?.isSupported ?? true,
          value,
        },
      });
    };
  }, [settings, onChange]);

  // Check if we have any settings to show
  const hasAnySettings = useMemo(() => {
    return Boolean(
      settings.timeout?.isSupported ||
      settings.concurrency?.isSupported ||
      settings.splitOn?.isSupported ||
      settings.count?.isSupported ||
      settings.conditionExpressions?.isSupported ||
      settings.retryPolicy?.isSupported ||
      settings.asynchronous?.isSupported ||
      settings.disableAsyncPattern?.isSupported ||
      settings.requestOptions?.isSupported ||
      settings.paging?.isSupported ||
      settings.uploadChunk?.isSupported ||
      settings.downloadChunkSize?.isSupported ||
      settings.suppressWorkflowHeaders?.isSupported ||
      settings.suppressWorkflowHeadersOnResponse?.isSupported ||
      settings.secureInputs?.isSupported ||
      settings.secureOutputs?.isSupported ||
      settings.trackedProperties?.isSupported ||
      settings.correlation?.isSupported ||
      settings.requestSchemaValidation?.isSupported ||
      settings.disableAutomaticDecompression?.isSupported ||
      (settings.runAfter?.isSupported && !isTrigger)
    );
  }, [settings, isTrigger]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner size="small" />
          <Text>Loading settings...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Text weight="semibold">Failed to load settings</Text>
          <Text size={200}>{error}</Text>
        </div>
      </div>
    );
  }

  if (!hasAnySettings) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Text weight="semibold">No configurable settings</Text>
          <Text size={200}>
            This {isTrigger ? 'trigger' : 'action'} does not have any configurable settings.
          </Text>
        </div>
      </div>
    );
  }

  // Count validation errors for display
  const errorCount = Object.keys(validationErrors).length;

  return (
    <div className={styles.container}>
      {/* Validation Error Summary */}
      {errorCount > 0 && (
        <MessageBar intent="error" className={styles.errorBanner}>
          <MessageBarBody>
            <ErrorCircleRegular className={styles.errorIcon} />
            {errorCount} {errorCount === 1 ? 'error' : 'errors'} found. Fix before saving.
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.scrollContainer}>
        {/* General Settings */}
        {(settings.timeout?.isSupported || settings.concurrency?.isSupported || settings.splitOn?.isSupported || settings.count?.isSupported || settings.conditionExpressions?.isSupported) && (
          <Text className={styles.sectionHeader}>General</Text>
        )}

        {settings.timeout?.isSupported && (
          <div className={styles.settingCard} data-testid="settings-section-general">
            <TimeoutRenderer
              setting={settings.timeout}
              onChange={createChangeHandler<string>('timeout')}
              readOnly={readOnly}
              label="Timeout"
            />
          </div>
        )}

        {settings.concurrency?.isSupported && (
          <div className={styles.settingCard}>
            <ConcurrencyRenderer
              setting={settings.concurrency}
              onChange={createChangeHandler<ConcurrencySettings>('concurrency')}
              readOnly={readOnly}
              isTrigger={isTrigger}
            />
          </div>
        )}

        {settings.splitOn?.isSupported && isTrigger && (
          <div className={styles.settingCard}>
            <SplitOnRenderer
              setting={settings.splitOn}
              onChange={createChangeHandler<SimpleSetting<string>>('splitOn')}
              readOnly={readOnly}
              allowedValues={settings.splitOnAllowedValues}
            />
          </div>
        )}

        {settings.count?.isSupported && (
          <div className={styles.settingCard}>
            <CountRenderer
              setting={settings.count}
              onChange={createChangeHandler<string | number>('count')}
              readOnly={readOnly}
            />
          </div>
        )}

        {settings.conditionExpressions?.isSupported && isTrigger && (
          <div className={styles.settingCard}>
            <ConditionsRenderer
              setting={settings.conditionExpressions}
              onChange={createChangeHandler<string[]>('conditionExpressions')}
              readOnly={readOnly}
            />
          </div>
        )}

        {/* Networking Settings */}
        {(settings.retryPolicy?.isSupported || settings.requestOptions?.isSupported || settings.asynchronous?.isSupported || settings.disableAsyncPattern?.isSupported || settings.paging?.isSupported || settings.uploadChunk?.isSupported || settings.downloadChunkSize?.isSupported || settings.suppressWorkflowHeaders?.isSupported || settings.suppressWorkflowHeadersOnResponse?.isSupported) && (
          <Text className={styles.sectionHeader}>Networking</Text>
        )}

        {settings.retryPolicy?.isSupported && (
          <div className={styles.settingCard} data-testid="settings-section-networking">
            <RetryPolicyRenderer
              setting={settings.retryPolicy}
              onChange={createChangeHandler<RetryPolicy>('retryPolicy')}
              readOnly={readOnly}
            />
          </div>
        )}

        {settings.requestOptions?.isSupported && (
          <div className={styles.settingCard}>
            <RequestOptionsRenderer
              setting={settings.requestOptions}
              onChange={createChangeHandler<RequestOptions>('requestOptions')}
              readOnly={readOnly}
            />
          </div>
        )}

        {settings.asynchronous?.isSupported && (
          <div className={styles.settingCard}>
            <BooleanRenderer
              setting={settings.asynchronous}
              onChange={createChangeHandler<boolean>('asynchronous')}
              readOnly={readOnly}
              label="Asynchronous Pattern"
            />
          </div>
        )}

        {settings.disableAsyncPattern?.isSupported && (
          <div className={styles.settingCard}>
            <BooleanRenderer
              setting={settings.disableAsyncPattern}
              onChange={createChangeHandler<boolean>('disableAsyncPattern')}
              readOnly={readOnly}
              label="Disable Async Pattern"
            />
          </div>
        )}

        {settings.paging?.isSupported && (
          <div className={styles.settingCard}>
            <PagingRenderer
              setting={settings.paging}
              onChange={createChangeHandler<PagingSettings>('paging')}
              readOnly={readOnly}
            />
          </div>
        )}

        {settings.uploadChunk?.isSupported && (
          <div className={styles.settingCard}>
            <ChunkingRenderer
              setting={settings.uploadChunk}
              onChange={createChangeHandler<UploadChunk>('uploadChunk')}
              readOnly={readOnly}
              label="Chunked Upload"
            />
          </div>
        )}

        {settings.downloadChunkSize?.isSupported && (
          <div className={styles.settingCard}>
            <div className={styles.downloadChunkRow}>
              <Text className={styles.downloadChunkLabel}>Download Chunk Size</Text>
              <div className={styles.downloadChunkControls}>
                {settings.downloadChunkSize.value !== undefined && (
                  <>
                    <Field>
                      <SpinButton
                        value={settings.downloadChunkSize.value}
                        onChange={(_, data) => {
                          if (data.value !== undefined && data.value !== null) {
                            createChangeHandler<number>('downloadChunkSize')(data.value);
                          }
                        }}
                        min={1}
                        max={100}
                        step={1}
                        disabled={readOnly}
                        aria-label="Download chunk size in MB"
                      />
                    </Field>
                    <Text className={styles.downloadChunkUnit}>MB</Text>
                  </>
                )}
                <Switch
                  checked={settings.downloadChunkSize.value !== undefined}
                  onChange={(_, data) => {
                    if (data.checked) {
                      createChangeHandler<number>('downloadChunkSize')(10);
                    } else {
                      createChangeHandler<number>('downloadChunkSize')(undefined);
                    }
                  }}
                  disabled={readOnly}
                  aria-label="Enable chunked download"
                />
              </div>
            </div>
          </div>
        )}

        {settings.suppressWorkflowHeaders?.isSupported && (
          <div className={styles.settingCard}>
            <BooleanRenderer
              setting={settings.suppressWorkflowHeaders}
              onChange={createChangeHandler<boolean>('suppressWorkflowHeaders')}
              readOnly={readOnly}
              label="Suppress Workflow Headers"
            />
          </div>
        )}

        {settings.suppressWorkflowHeadersOnResponse?.isSupported && (
          <div className={styles.settingCard}>
            <BooleanRenderer
              setting={settings.suppressWorkflowHeadersOnResponse}
              onChange={createChangeHandler<boolean>('suppressWorkflowHeadersOnResponse')}
              readOnly={readOnly}
              label="Suppress Headers on Response"
            />
          </div>
        )}

        {/* Security Settings */}
        {(settings.secureInputs?.isSupported || settings.secureOutputs?.isSupported) && (
          <Text className={styles.sectionHeader}>Security</Text>
        )}

        {settings.secureInputs?.isSupported && (
          <div className={styles.settingCard} data-testid="settings-section-security">
            <BooleanRenderer
              setting={settings.secureInputs}
              onChange={createChangeHandler<boolean>('secureInputs')}
              readOnly={readOnly}
              label="Secure Inputs"
            />
          </div>
        )}

        {settings.secureOutputs?.isSupported && (
          <div className={styles.settingCard}>
            <BooleanRenderer
              setting={settings.secureOutputs}
              onChange={createChangeHandler<boolean>('secureOutputs')}
              readOnly={readOnly}
              label="Secure Outputs"
            />
          </div>
        )}

        {/* Tracking Settings */}
        {(settings.trackedProperties?.isSupported || settings.correlation?.isSupported) && (
          <Text className={styles.sectionHeader}>Tracking</Text>
        )}

        {settings.trackedProperties?.isSupported && (
          <div className={styles.settingCard} data-testid="settings-section-tracking">
            <TrackedPropertiesRenderer
              setting={settings.trackedProperties}
              onChange={createChangeHandler<Record<string, string>>('trackedProperties')}
              readOnly={readOnly}
            />
          </div>
        )}

        {settings.correlation?.isSupported && (
          <div className={styles.settingCard}>
            <CorrelationRenderer
              setting={settings.correlation}
              onChange={createChangeHandler<CorrelationSettings>('correlation')}
              readOnly={readOnly}
            />
          </div>
        )}

        {/* Data Handling Settings */}
        {(settings.requestSchemaValidation?.isSupported || settings.disableAutomaticDecompression?.isSupported) && (
          <Text className={styles.sectionHeader}>Data Handling</Text>
        )}

        {settings.requestSchemaValidation?.isSupported && (
          <div className={styles.settingCard} data-testid="settings-section-datahandling">
            <BooleanRenderer
              setting={settings.requestSchemaValidation}
              onChange={createChangeHandler<boolean>('requestSchemaValidation')}
              readOnly={readOnly}
              label="Schema Validation"
            />
          </div>
        )}

        {settings.disableAutomaticDecompression?.isSupported && (
          <div className={styles.settingCard}>
            <BooleanRenderer
              setting={settings.disableAutomaticDecompression}
              onChange={createChangeHandler<boolean>('disableAutomaticDecompression')}
              readOnly={readOnly}
              label="Disable Auto Decompression"
            />
          </div>
        )}

        {/* Run After Settings (Actions only) */}
        {settings.runAfter?.isSupported && !isTrigger && (
          <>
            <Text className={styles.sectionHeader}>Run After</Text>
            <div className={styles.settingCard} data-testid="settings-section-runafter">
              <RunAfterRenderer
                setting={settings.runAfter}
                onChange={createChangeHandler<RunAfterSettings>('runAfter')}
                readOnly={readOnly}
                label=""
                availableActions={availablePredecessors}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

SettingsPanel.displayName = 'SettingsPanel';
